<template>
    <div class="bonusomat-automat-cylinder" ref="container">
        <div class="bonusomat-automat-cylinder__card" v-for="(iconUrl, index) in iconsUrls" :key="`wh-i${index}`">
            <WebpImage
                :alt="`Cylinder item ${index}`"
                cssClass="bonusomat-automat-cylinder__card-image"
                :src="iconUrl"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import WebpImage from "@sazka/design-system/src/image/webp-image.vue";
import { computed, watch } from "vue";
import { KeenSliderInstance, useKeenSlider } from "keen-slider/vue.es";
import { AutomatCylinderStoppingPhase, SpinAutomatState } from "@/casino/bonusomat/models/bonusomat-spin-types";

const emit = defineEmits(["spinEnd"]);

const props = defineProps<{
    iconsUrls: string[];
    state: SpinAutomatState;
    winIcon: string | undefined;
    spinEndDelay: number;
}>();

const oneSlideChangeDuration = 80;

const oneRoundIndex = computed(() => props.iconsUrls.length * -1);
const oneRoundAnimationSettings = computed(() => {
    return { duration: oneSlideChangeDuration * props.iconsUrls.length, easing: (t) => t };
});

const winIndex = computed(() => props.iconsUrls.indexOf(props.winIcon) - props.iconsUrls.length);
const prepareIndexAnimationSettings = computed(() => {
    return { duration: oneSlideChangeDuration * winIndex.value, easing: (t) => t };
});

const finishSlideAnimationSettings = computed(() => {
    return {
        duration: oneSlideChangeDuration * props.iconsUrls.length * 2 + props.spinEndDelay,
        easing: (t) => (t -= 1) * t * t + 1,
    };
});

let sliderPositionMemory = 0;
let sliderSettingMemory = null;
const setNextSpin = (slider: KeenSliderInstance, increaseIdxBy: number, currentSettings) => {
    sliderPositionMemory = slider.track.details.abs + increaseIdxBy;
    sliderSettingMemory = currentSettings;
    slider.moveToIdx(sliderPositionMemory, true, sliderSettingMemory);
};

const restoreCurrentSpin = (slider: KeenSliderInstance) => {
    slider.moveToIdx(sliderPositionMemory, true, sliderSettingMemory);
};

let stoppingPhase: AutomatCylinderStoppingPhase = "PREPARE_INDEX";
const spinCylinderMachineState = (slider: KeenSliderInstance) => {
    if (props.state == "ROLLING") {
        // ROLLING is spinning round and round at full speed
        setNextSpin(slider, oneRoundIndex.value, oneRoundAnimationSettings.value);
        console.log(props.state, stoppingPhase, slider.track.details.abs, sliderPositionMemory);
    } else if (props.state == "STOPPING") {
        if (stoppingPhase == "PREPARE_INDEX") {
            // PREPARE INDEX phase turn around to the end position at full speed
            setNextSpin(slider, winIndex.value, prepareIndexAnimationSettings.value);
            console.log(props.state, stoppingPhase, slider.track.details.abs, sliderPositionMemory);
            stoppingPhase = "FINISH_SLIDE";
        } else if (stoppingPhase == "FINISH_SLIDE") {
            // FINISH_SLIDE phase spins 1 round with inertia
            setNextSpin(slider, oneRoundIndex.value, finishSlideAnimationSettings.value);
            console.log(props.state, stoppingPhase, slider.track.details.abs, sliderPositionMemory);
            stoppingPhase = "TRIGGER_END";
        } else {
            // TRIGGER_END finishes the process
            emit("spinEnd");
            stoppingPhase = "PREPARE_INDEX";
        }
    }
};

const [container, keenSlider] = useKeenSlider({
    loop: true,
    vertical: true,
    drag: false,
    mode: "free-snap",
    renderMode: "performance",
    slides: { perView: "auto", spacing: 0 },
    selector: ".bonusomat-automat-cylinder__card",

    animationEnded(slider: KeenSliderInstance) {
        spinCylinderMachineState(slider);
    },

    updated(slider: KeenSliderInstance) {
        // restore slider spin
        if (props.state == "ROLLING" || props.state == "STOPPING") {
            if (slider.track.details.abs == sliderPositionMemory) {
                spinCylinderMachineState(slider);
            } else {
                console.log("restore", slider.track.details.abs, sliderPositionMemory);
                restoreCurrentSpin(slider);
            }
        }
    },
});

watch(
    () => props.state,
    (newState) => {
        if (newState == "ROLLING") {
            spinCylinderMachineState(keenSlider.value);
        }
    }
);
</script>
